/* ==========================================================================
   NEFER Studio — motion system: "Beauty that breathes"
   assets/nefer-motion.js — include deferred, at the end of </body> or:
     <script src="{{ 'nefer-motion.js' | asset_url }}" defer></script>

   Two ideas, from the brand's own words:
     1. The Breath — slow calm easing, 500–900ms, one house curve.
     2. The Sketch — "every piece begins as a sketch": SVG line-draw.

   Rule: if an animation is noticeable AS an animation, it's too much.

   Markup contract (add these attributes in Liquid):
     data-nf-reveal                 fade + 16px rise
     data-nf-reveal="image"         fade + scale 1.04 → 1
     data-nf-reveal="draw"          line-draws child [data-nf-stroke] paths
     data-nf-reveal="count"         counts 0 → data-nf-count
     data-nf-delay="80"             stagger in ms (siblings: 0, 80, 160…)
     data-nf-stroke="180"           per-path draw delay inside a draw group
     data-nf-header                 the sticky header
     data-nf-hero                   the home hero (presence = transparent header)
     data-nf-intro                  the first-visit overlay
   ========================================================================== */

(function () {
  'use strict';

  var EASE = 'cubic-bezier(.22,1,.36,1)';
  var reduced = false;
  try {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  /* ------------------------------------------------------------------ *
   * 1. Scroll reveals
   *    Hidden state is set FROM JS, never in CSS — if this file fails to
   *    load, every element must still be visible. Non-negotiable.
   * ------------------------------------------------------------------ */

  var io = null;

  function prime(el) {
    if (el.__nf) return;
    el.__nf = 1;
    var kind = el.getAttribute('data-nf-reveal');
    if (kind === 'draw') {
      el.style.opacity = '0';
      each(el.querySelectorAll('[data-nf-stroke]'), function (p) {
        p.style.strokeDasharray = '1';       /* paths carry pathLength="1" */
        p.style.strokeDashoffset = '1';
      });
    } else if (kind !== 'count') {
      el.style.opacity = '0';
    }
    io.observe(el);
  }

  function fire(el) {
    var kind = el.getAttribute('data-nf-reveal');
    var delay = parseInt(el.getAttribute('data-nf-delay') || '0', 10);

    if (kind === 'count') { countUp(el); return; }

    if (kind === 'draw') {
      el.style.opacity = '1';
      each(el.querySelectorAll('[data-nf-stroke]'), function (p) {
        var d = parseInt(p.getAttribute('data-nf-stroke') || '0', 10);
        if (reduced) { p.style.strokeDashoffset = '0'; return; }
        p.style.animation = 'nf-draw 900ms ' + EASE + ' ' + (delay + d) + 'ms forwards';
      });
      return;
    }

    var anim = reduced
      ? 'nf-fade 400ms linear'
      : (kind === 'image' ? 'nf-settle 1000ms ' + EASE : 'nf-rise 780ms ' + EASE);
    el.style.animation = anim + ' ' + delay + 'ms both';
  }

  function initReveals(root) {
    root = root || document;
    if (!('IntersectionObserver' in window)) {
      each(root.querySelectorAll('[data-nf-reveal]'), function (el) {
        el.style.opacity = '1';
        if (el.getAttribute('data-nf-reveal') === 'count') countUp(el, true);
      });
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        each(entries, function (en) {
          if (!en.isIntersecting) return;
          io.unobserve(en.target);
          fire(en.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    }
    each(root.querySelectorAll('[data-nf-reveal]'), prime);
  }

  /* ------------------------------------------------------------------ *
   * 2. Hours-of-handwork count-up
   *    <span data-nf-reveal="count" data-nf-count="{{ product.metafields
   *      .custom.hours_of_handwork }}">{{ ...same value... }}</span>
   *    The number is in the markup so it survives with JS off.
   * ------------------------------------------------------------------ */

  function countUp(el, instant) {
    var target = parseInt(el.getAttribute('data-nf-count') || el.textContent || '0', 10);
    if (isNaN(target)) return;
    if (reduced || instant) { el.textContent = String(target); return; }
    var dur = 1500, t0 = 0;
    el.textContent = '0';
    requestAnimationFrame(function step(t) {
      if (!t0) t0 = t;
      var k = Math.min(1, (t - t0) / dur);
      el.textContent = String(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) requestAnimationFrame(step);
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Header — transparent over the hero, cream once scrolled
   *    Only the home template renders [data-nf-hero]; elsewhere the
   *    header stays cream from the first frame.
   * ------------------------------------------------------------------ */

  function initHeader() {
    var header = document.querySelector('[data-nf-header]');
    if (!header) return;
    var overHero = !!document.querySelector('[data-nf-hero]');
    var ticking = false;

    function apply() {
      var y = window.scrollY || document.documentElement.scrollTop || 0;
      var past = y > 40;
      header.classList.toggle('nf-header--scrolled', past);
      if (overHero) header.classList.toggle('nf-header--over', !past);
      ticking = false;
    }

    if (overHero) header.classList.add('nf-header--over');
    apply();
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ *
   * 4. First-visit intro
   *    Once per SESSION. Never on navigation. Must not delay content
   *    paint — the page renders underneath; this only sits on top.
   *
   *    <div data-nf-intro>  (cream, fixed, z-index 200, pointer-events:none)
   *      <div class="nf-glyph nf-glyph--draw"> …paths with pathLength="1"… </div>
   *      <div class="nf-intro__word">Nefer Studio</div>
   *    </div>
   * ------------------------------------------------------------------ */

  var INTRO_TOTAL = 2900;   /* draw ~1.5s, hold ~600ms, then fade out */

  function initIntro() {
    var intro = document.querySelector('[data-nf-intro]');
    if (!intro) return;

    var seen = false;
    try { seen = !!sessionStorage.getItem('nefer-intro'); } catch (e) {}
    if (seen || reduced) { intro.parentNode && intro.parentNode.removeChild(intro); return; }

    intro.style.animation = 'nf-introout ' + INTRO_TOTAL + 'ms ' + EASE + ' both';

    /* draw order: stem, oval, crossbar, inner arc, base arch */
    each(intro.querySelectorAll('[data-nf-stroke]'), function (p) {
      var d = parseInt(p.getAttribute('data-nf-stroke') || '0', 10);
      p.style.strokeDasharray = '1';
      p.style.strokeDashoffset = '1';
      p.style.animation = 'nf-draw 900ms ' + EASE + ' ' + (120 + d) + 'ms forwards';
    });

    var glyph = intro.querySelector('.nf-glyph');
    if (glyph) glyph.style.animation = 'nf-breathe 6s ease-in-out 1100ms infinite';

    var word = intro.querySelector('.nf-intro__word');
    if (word) word.style.animation = 'nf-fade 800ms ' + EASE + ' 1400ms both';

    setTimeout(function () {
      if (intro.parentNode) intro.parentNode.removeChild(intro);
      try { sessionStorage.setItem('nefer-intro', '1'); } catch (e) {}
    }, INTRO_TOTAL);
  }

  /* ------------------------------------------------------------------ *
   * 5. Product gallery — crossfade, never slide
   *    <div data-nf-gallery>
   *      <div data-nf-slide class="is-active"><img …></div> …
   *      <button data-nf-thumb="0">…</button> …
   * ------------------------------------------------------------------ */

  function initGallery() {
    each(document.querySelectorAll('[data-nf-gallery]'), function (g) {
      var slides = g.querySelectorAll('[data-nf-slide]');
      var thumbs = g.querySelectorAll('[data-nf-thumb]');
      if (!slides.length) return;

      function show(i) {
        each(slides, function (s, k) { s.style.opacity = k === i ? '1' : '0'; });
        each(thumbs, function (t, k) { t.setAttribute('aria-current', k === i ? 'true' : 'false'); });
      }

      each(slides, function (s, k) {
        s.style.transition = 'opacity 900ms ' + EASE;
        s.style.opacity = k === 0 ? '1' : '0';
      });
      each(thumbs, function (t, k) {
        t.addEventListener('click', function (e) { e.preventDefault(); show(k); });
      });
      show(0);
    });
  }

  /* ------------------------------------------------------------------ *
   * 6. Overlays — cart drawer + mobile menu
   *    Focus trap, Escape to close, scroll lock. The drawer's contents
   *    are re-rendered by the Section Rendering API; call
   *    NeferMotion.refresh() after you swap the HTML in.
   * ------------------------------------------------------------------ */

  var lastFocus = null;

  function openOverlay(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.removeAttribute('hidden');
    document.documentElement.style.overflow = 'hidden';
    var f = el.querySelector('button, [href], input, select, textarea');
    if (f) f.focus();
  }

  function closeOverlay(el) {
    if (!el) return;
    el.setAttribute('hidden', '');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function initOverlays() {
    each(document.querySelectorAll('[data-nf-open]'), function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openOverlay(document.querySelector('#' + btn.getAttribute('data-nf-open')));
      });
    });
    each(document.querySelectorAll('[data-nf-close]'), function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeOverlay(btn.closest('[data-nf-overlay]'));
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      each(document.querySelectorAll('[data-nf-overlay]:not([hidden])'), closeOverlay);
    });
    /* click the scrim to dismiss */
    each(document.querySelectorAll('[data-nf-scrim]'), function (s) {
      s.addEventListener('click', function () { closeOverlay(s.closest('[data-nf-overlay]')); });
    });
  }

  /* ------------------------------------------------------------------ *
   * helpers + boot
   * ------------------------------------------------------------------ */

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* ------------------------------------------------------------------ *
   * 7. Ambient film
   *    `muted` as an ATTRIBUTE does not satisfy Chrome's autoplay policy —
   *    play() rejects and you get a still frame with nothing in the console.
   *    The property has to be set from JS. Catch the rejection: a blocked
   *    autoplay is not a page error, and a poster frame is a fine outcome.
   *
   *    Under reduced motion the film is PAUSED ON ITS POSTER, never hidden.
   * ------------------------------------------------------------------ */

  function initFilm(scope) {
    each((scope || document).querySelectorAll('[data-nf-film]'), function (v) {
      if (v.__nf) return;
      v.__nf = 1;

      v.muted = true;
      v.defaultMuted = true;

      if (reduced) { v.autoplay = false; v.pause(); return; }

      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
  }

  /* ------------------------------------------------------------------ *
   * 8. Product-card swipe dots
   *    The swipe itself is CSS scroll-snap, not JS — this only mirrors
   *    the track's position into the dots. If this never runs the card
   *    still swipes; you just lose the position indicator.
   * ------------------------------------------------------------------ */

  function initCards(root) {
    each((root || document).querySelectorAll('[data-nf-card-track]'), function (track) {
      if (track.__nfCard) return;
      track.__nfCard = 1;

      var dots = track.parentNode.querySelector('[data-nf-card-dots]');
      if (!dots || !dots.children.length) return;
      var marks = dots.children;
      marks[0].className = 'is-on';

      function sync() {
        queued = false;
        var w = track.clientWidth || 1;
        var at = Math.round(track.scrollLeft / w);
        for (var i = 0; i < marks.length; i++) {
          marks[i].className = i === at ? 'is-on' : '';
        }
      }

      /* Scroll fires per frame on a touch drag; coalesce so a grid of
         sixteen cards cannot turn one swipe into sixteen layout reads.

         rAF is raced against a timer rather than used alone: rAF does not
         run in a background tab, and a card left mid-swipe when the tab
         was backgrounded would come back with its dots on the wrong slide.
         Whichever fires first wins — sync() is idempotent. */
      var queued = false;
      track.addEventListener('scroll', function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(sync);
        setTimeout(sync, 120);
      }, { passive: true });
    });
  }

  function boot() {
    initIntro();
    initHeader();
    initReveals();
    initGallery();
    initOverlays();
    initFilm();
    initCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    initGallery();
    initOverlays();
    initHeader();
    initFilm(e.target);
    initCards(e.target);
  });

  window.NeferMotion = {
    refresh: function (root) { initReveals(root); initGallery(); initOverlays(); initFilm(root); initCards(root); },
    open: openOverlay,
    close: closeOverlay,
    reduced: reduced,
    EASE: EASE
  };
})();
